import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from "@/components/ui/use-toast";
import FloatingActionButton from '@/components/FloatingActionButton';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { Home, Receipt, Wallet, Settings } from 'lucide-react';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [selectedWallet, setSelectedWallet] = useState<any>(null);
  const [wallets, setWallets] = useState<any[]>([]);

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            title: "Error",
            description: "Please sign in to continue",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }

        const { data, error } = await supabase
          .from('wallets')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setWallets(data || []);
        if (data && data.length > 0) {
          setSelectedWallet(data[0]);
        }
      } catch (error) {
        console.error('Error fetching wallets:', error);
        toast({
          title: "Error",
          description: "Failed to fetch wallets",
          variant: "destructive",
        });
      }
    };

    fetchWallets();
  }, [navigate, toast]);

  const handleTabChange = (value: string) => {
    navigate(value);
  };

  const handleTransactionSubmit = async (data: any) => {
    try {
      if (!selectedWallet) {
        toast({
          title: "Error",
          description: "Please select a wallet first",
          variant: "destructive",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "Please sign in to continue",
          variant: "destructive",
        });
        return;
      }

      const transaction = {
        ...data,
        wallet_id: selectedWallet.id,
        user_id: user.id,
        created_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('transactions')
        .insert(transaction);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Transaction added successfully",
      });

      // Update wallet balance
      const amount = transaction.type === 'expense' ? -transaction.amount : transaction.amount;
      const { error: updateError } = await supabase
        .from('wallets')
        .update({ balance: selectedWallet.balance + amount })
        .eq('id', selectedWallet.id);

      if (updateError) throw updateError;

      // Refresh wallet data
      const { data: updatedWallet, error: fetchError } = await supabase
        .from('wallets')
        .select('*')
        .eq('id', selectedWallet.id)
        .single();

      if (fetchError) throw fetchError;
      setSelectedWallet(updatedWallet);

    } catch (error) {
      console.error('Error submitting transaction:', error);
      toast({
        title: "Error",
        description: "Failed to submit transaction",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="flex flex-col h-screen">
        {/* Mobile Header */}
        <div className="md:hidden border-b bg-background sticky top-0 z-50">
          <div className="flex items-center justify-between p-4">
            <span className="text-xl font-bold">Pramdana</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b bg-background sticky top-0 z-50">
          <div className="container mx-auto px-4">
            <Tabs 
              defaultValue={location.pathname} 
              className="w-full" 
              onValueChange={handleTabChange}
            >
              <TabsList className="grid w-full grid-cols-4 h-12">
                <TabsTrigger 
                  value="/dashboard" 
                  className={cn(
                    "flex flex-col items-center gap-1 h-full",
                    "data-[state=active]:bg-[#0B2447] data-[state=active]:text-white"
                  )}
                >
                  <Home className="h-4 w-4" />
                  <span className="text-xs">Overview</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="/dashboard/transactions" 
                  className={cn(
                    "flex flex-col items-center gap-1 h-full",
                    "data-[state=active]:bg-[#0B2447] data-[state=active]:text-white"
                  )}
                >
                  <Receipt className="h-4 w-4" />
                  <span className="text-xs">Transactions</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="/dashboard/wallets" 
                  className={cn(
                    "flex flex-col items-center gap-1 h-full",
                    "data-[state=active]:bg-[#0B2447] data-[state=active]:text-white"
                  )}
                >
                  <Wallet className="h-4 w-4" />
                  <span className="text-xs">Wallets</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="/dashboard/settings" 
                  className={cn(
                    "flex flex-col items-center gap-1 h-full",
                    "data-[state=active]:bg-[#0B2447] data-[state=active]:text-white"
                  )}
                >
                  <Settings className="h-4 w-4" />
                  <span className="text-xs">Settings</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto relative pb-16 md:pb-0">
          <div className="container mx-auto p-4 md:p-6">
            <Outlet />
          </div>

          {/* Floating Action Button */}
          <FloatingActionButton
            onTransactionSubmit={handleTransactionSubmit}
            onWalletSelect={(wallet) => setSelectedWallet(wallet)}
            wallets={wallets}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout; 